// App.tsx
import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from './store/authStore';
import { authService } from './services/authService';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import HomePage from './pages/HomePage';
import SubjectsPage from './pages/SubjectsPage';
import ChaptersPage from './pages/ChaptersPage';
import LessonsPage from './pages/LessonsPage';
import ExploreFeedPage from './pages/ExploreFeedPage';
import PlayerPage from './pages/PlayerPage';
import ProtectedRoute from './components/auth/ProtectedRoute';

function App() {
  const setUser = useAuthStore((state) => state.setUser);

  useEffect(() => {
    // 1. Auth State Sync
    const unsubscribe = authService.onAuthStateChange((user) => {
      setUser(user);
    });

    // 2. App Start Version Check (Single Ping to Hosting)
    const checkVersion = async () => {
      try {
        const response = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' });
        if (!response.ok) return;
        
        const data = await response.json();
        const localVersion = localStorage.getItem('app_version');

        if (localVersion && data.version !== localVersion) {
          console.log('🔄 New version detected. Flushing cache...');
          
          // Flush all caches
          if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
          }
          
          // Update version and reload
          localStorage.setItem('app_version', data.version);
          window.location.reload();
        } else if (!localVersion) {
          localStorage.setItem('app_version', data.version);
        }
      } catch (err) {
        console.warn('Version check failed (offline or network error):', err);
      }
    };

    checkVersion();

    return () => unsubscribe();
  }, [setUser]);

  return (
    <Routes>
      {/* Auth */}
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      
      {/* Home */}
      <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      
      {/* Course Flow: Subjects → Chapters → Lessons */}
      <Route path="/course" element={<ProtectedRoute><SubjectsPage /></ProtectedRoute>} />
      <Route path="/course/:topicId/:subjectId" element={<ProtectedRoute><ChaptersPage /></ProtectedRoute>} />
      <Route path="/course/:topicId/:subjectId/:chapterId" element={<ProtectedRoute><LessonsPage /></ProtectedRoute>} />
      
      {/* Explore Reels */}
      <Route path="/explore" element={<ProtectedRoute><ExploreFeedPage /></ProtectedRoute>} />
      
      {/* Shared Player */}
      <Route path="/watch/:videoId" element={<PlayerPage />} />
      
      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
